import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { ImageUp, Trash2 } from "lucide-react";
import Notice from "components/tw/notice";
import { btnSecondaryCls } from "components/tw/classes";
import {
  deleteCompanyLogo,
  fetchCompanyLogo,
  uploadCompanyLogo,
} from "services/company.service";
import { SettingsCard } from "./ui";

// The backend refuses the same things; checking here saves a round trip and
// gives the message before anything is uploaded.
const LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml"];
const MAX_LOGO_BYTES = 300 * 1024;

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * The logo printed at the top left of the company's sales invoices: preview,
 * upload and remove. Lives on the company settings page, which only a Master
 * can open, and the backend checks that again.
 *
 * The preview changes only after the backend accepted the change, so what is
 * on screen is what the next invoice will print.
 */
export default function CompanyLogoBlock({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "failed">("loading");
  // The saved logo as a data URL, or null when none is uploaded.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setLoadState("loading");
    fetchCompanyLogo(companyId)
      .then((res: { logo: { dataUrl: string } | null }) => {
        setLogoUrl(res.logo ? res.logo.dataUrl : null);
        setLoadState("ready");
      })
      .catch(() => setLoadState("failed"));
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveChange = async (change: () => Promise<void>) => {
    setSaving(true);
    setError("");
    try {
      await change();
    } catch (e: any) {
      // The server answers a refusal with the bare code string as the body.
      const body = e?.response?.data;
      const code = typeof body === "string" ? body : body?.code;
      if (code === "LOGO_TOO_LARGE") setError(t("company_logo_too_large"));
      else if (code === "INVALID_LOGO") setError(t("company_logo_wrong_type"));
      else setError(t("company_logo_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File) => {
    if (!LOGO_TYPES.includes(file.type)) {
      setError(t("company_logo_wrong_type"));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t("company_logo_too_large"));
      return;
    }
    await saveChange(async () => {
      const dataUrl = await readAsDataUrl(file);
      await uploadCompanyLogo(companyId, dataUrl);
      setLogoUrl(dataUrl);
    });
  };

  const remove = () =>
    saveChange(async () => {
      await deleteCompanyLogo(companyId);
      setLogoUrl(null);
    });

  const handleFileChosen = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so choosing the same file again still fires a change.
    e.target.value = "";
    if (file) upload(file);
  };

  return (
    <SettingsCard title={t("company_logo_title")} description={t("company_logo_hint")}>
      {loadState === "loading" && <p className="text-sm text-gray-500">{t("loading")}</p>}

      {loadState === "failed" && (
        <Notice tone="critical">
          <p>{t("company_logo_load_failed")}</p>
          <button type="button" className={btnSecondaryCls} onClick={load}>
            {t("retry")}
          </button>
        </Notice>
      )}

      {loadState === "ready" && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Same 176×64 box the invoice prints the logo in. */}
          {logoUrl ? (
            <div className="flex h-16 w-44 items-center justify-center rounded-md border border-gray-200">
              <img
                src={logoUrl}
                alt={t("company_logo_title")}
                className="h-full w-full object-contain"
              />
            </div>
          ) : (
            <div className="flex h-16 w-44 items-center justify-center rounded-md border-2 border-dashed border-gray-300 text-xs text-gray-400">
              {t("company_logo_none")}
            </div>
          )}

          <div className="flex gap-2">
            <input
              ref={fileInput}
              type="file"
              accept={LOGO_TYPES.join(",")}
              className="hidden"
              data-testid="company-logo-file-input"
              onChange={handleFileChosen}
            />
            <button
              type="button"
              className={btnSecondaryCls}
              disabled={saving}
              onClick={() => fileInput.current?.click()}
            >
              <ImageUp className="size-4" aria-hidden="true" />
              {t("company_logo_upload")}
            </button>
            {logoUrl && (
              <button type="button" className={btnSecondaryCls} disabled={saving} onClick={remove}>
                <Trash2 className="size-4" aria-hidden="true" />
                {t("company_logo_remove")}
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3">
          <Notice tone="critical" onDismiss={() => setError("")} dismissLabel={t("close")}>
            <p>{error}</p>
          </Notice>
        </div>
      )}
    </SettingsCard>
  );
}
